import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
} from 'rxjs';
import { TuiTime } from '@taiga-ui/cdk';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import * as moment from 'moment';

import { HistoryServicesService } from 'src/app/services/history-services/history-services.service';

import {
  ORDER_STATUS_MAP,
  OrderStatusMeta,
  STATUS_CODE_MAP,
} from 'src/app/constants/order-status';

import {
  GetPaginatedFreelancePoolServicesRequest,
  PoolServiceItem,
} from '../pool-management/interface/pool-service.interface';
import { ServicesService } from 'src/app/services/service/services.service';
import { TuiAlertService } from '@taiga-ui/core';
import { Zone } from 'src/app/models/zone/zone.model';
import { ProductFilterItem } from 'src/app/models/pending-services/pending-services.model';
import {
  applyTimeFormat,
  DRIVER_STAGE_COLUMNS,
  getDriverStageTimes,
  timeToExcelDay,
} from 'src/app/utils/driver-events.helper';

type SortDirection = -1 | 1;

type SortableColumn =
  | 'licensePlate'
  | 'orderHash'
  | 'zoneName'
  | 'assignedStartDate'
  | 'startHour'
  | 'productName'
  | 'nodeStatus'
  | 'clientName'
  | 'driverName'
  | 'totalPrice';

type TableColumnKey =
  | 'date'
  | 'time'
  | 'licensePlate'
  | 'orderHash'
  | 'zone'
  | 'product'
  | 'client'
  | 'status'
  | 'driver'
  | 'totalPrice'
  | 'actions';

@Component({
  selector: 'app-historical-table',
  templateUrl: './historical-table.component.html',
  styleUrls: ['./historical-table.component.css'],
})
export class HistoricalTableComponent implements OnInit {
  @Input() useApiFiltering = false;
  private _totalItems = 0;
  private _currentPage = 0;
  private _serverPageSize = 25;

  @Input()
  set totalItems(value: number) {
    this._totalItems = Number.isFinite(value) ? Number(value) : 0;
    this.syncApiPaginationState();
  }
  get totalItems(): number {
    return this._totalItems;
  }

  @Input()
  set currentPage(value: number) {
    this._currentPage = Number.isFinite(value) ? Number(value) : 0;
    this.syncApiPaginationState();
  }
  get currentPage(): number {
    return this._currentPage;
  }

  @Input()
  set serverPageSize(value: number) {
    const normalizedValue = Number(value);
    this._serverPageSize = normalizedValue > 0 ? normalizedValue : 25;
    this.syncApiPaginationState();
  }
  get serverPageSize(): number {
    return this._serverPageSize;
  }

  @Input()
  set data(value: ReadonlyArray<PoolServiceItem> | null) {
    this.allServices = Array.isArray(value) ? [...value] : [];
    this.rebuildLookupsAndRefresh();
  }

  private _isLoading = false;
  isLoadingProducts: boolean = false;
  @Input()
  set isLoading(value: boolean) {
    this._isLoading = !!value;
    this.isLoading$.next(this._isLoading);
    this.toggleFiltersDisabled(this._isLoading);
  }
  get isLoading(): boolean {
    return this._isLoading;
  }

  @Output() countEvent = new EventEmitter<number>();
  @Output()
  filtersChange = new EventEmitter<GetPaginatedFreelancePoolServicesRequest>();

  index = 0;
  length = 0;
  readonly pageSize = 25;

  readonly sizes = ['l', 'm', 's'] as const;
  size = this.sizes[0];

  readonly columns: ReadonlyArray<TableColumnKey> = [
    'date',
    'time',
    'licensePlate',
    'orderHash',
    'zone',
    'product',
    'client',
    'status',
    'driver',
    'totalPrice',
    'actions',
  ];

  open = false;
  exportXlsx = false;
  exportCsv = false;

  private allServices: PoolServiceItem[] = [];
  paginatedData: PoolServiceItem[] = [];

  private sortDirection: SortDirection = 1;
  private sortedColumn: SortableColumn | '' = '';

  zones: Zone[] = [];
  products: ProductFilterItem[] = [];
  allClients: { clientName: string; clientId?: string }[] = [];
  allDrivers: { driverName: string }[] = [];

  readonly orderStatus = Object.entries(STATUS_CODE_MAP).map(([code, key]) => ({
    nodeStatus: Number(code),
    statusName: ORDER_STATUS_MAP[key]?.label ?? 'Desconocido',
  }));

  private readonly defaultDateRange = this.getDefaultDateRange();
  private previousValidDateRange: [Date, Date] = [...this.defaultDateRange];

  filtersForm: FormGroup = this.fb.group({
    search: ['' as string],
    dateRange: [this.defaultDateRange as [Date, Date]],
    zones: [[] as string[] | null],
    products: [[] as string[]],
    clients: [[] as string[]],
    drivers: [[] as string[]],
    status: [[] as number[]],
    startTime: [null as TuiTime | null],
    endTime: [null as TuiTime | null],
  });

  startTimeItems: TuiTime[] = this.generateTimeItems();
  endTimeItems: TuiTime[] = [];

  readonly isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly servicesSvc: ServicesService,
    private readonly historyServicesSvc: HistoryServicesService,
    private readonly translate: TranslateService,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly tuiAlertService: TuiAlertService
  ) {}

  ngOnInit(): void {
    this.loadZones();
    this.getProductsFilter();

    this.setupFormSubscriptions();
    this.toggleFiltersDisabled(this.isLoading);
    this.applyAllFiltersAndPaginate();
  }

  private loadZones(): void {
    this.servicesSvc.getZones().subscribe({
      next: (res) => {
        this.zones = res.zones;
      },
      error: (err) => {
        this.tuiAlertService
          .open(
            this.translate.instant('historical_table.zones_load_error_message'),
            {
              label: this.translate.instant(
                'historical_table.zones_load_error_title'
              ),
              status: 'error',
              autoClose: true,
            }
          )
          .subscribe();
      },
    });
  }

  async getProductsFilter(): Promise<void> {
    this.isLoadingProducts = true;
    this.products = await firstValueFrom(
      this.historyServicesSvc.getProductsForFilters()
    );

    this.products = this.products.map(
      (product: ProductFilterItem): ProductFilterItem => {
        product.ProductKey = this.translateProductKey(product.ProductKey);
        return product;
      }
    );
    this.isLoadingProducts = false;
  }

  private rebuildLookupsAndRefresh(): void {
    // this.buildProducts();
    this.buildAllClients();
    this.buildAllDrivers();

    this.index = this.useApiFiltering ? this.currentPage : 0;
    this.applyAllFiltersAndPaginate();
  }

  // private buildProducts(): void {
  //   const unique = new Set(
  //     (this.allServices || [])
  //       .map((s) => s.productName || '')
  //       .filter((p) => p && p.trim().length > 0)
  //       .map((p) => this.translateProduct(p))
  //   );

  //   this.products = Array.from(unique)
  //     .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
  //     .map((product) => ({ product }));
  // }

  private buildAllClients(): void {
    const clientsMap = new Map<string, { clientName: string; clientId?: string }>();

    (this.allServices || []).forEach((service) => {
      if (!service.clientName) return;

      const key = service.clientId || service.clientName;
      if (!clientsMap.has(key)) {
        clientsMap.set(key, {
          clientName: service.clientName,
          clientId: service.clientId,
        });
      }
    });

    this.allClients = Array.from(clientsMap.values()).sort((a, b) =>
      a.clientName.localeCompare(b.clientName, 'es', {
        sensitivity: 'base',
      })
    );
  }

  private buildAllDrivers(): void {
    const unassigned = this.translate.instant('historical_table.unassigned');
    const unique = new Set(
      (this.allServices || [])
        .map((s) => (s.driverName ?? '').trim())
        .filter((n) => n.length > 0)
    );

    this.allDrivers = [
      { driverName: unassigned },
      ...Array.from(unique)
        .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
        .map((driverName) => ({ driverName })),
    ];
  }

  private setupFormSubscriptions(): void {
    // Búsqueda con debounce
    this.filtersForm
      .get('search')
      ?.valueChanges.pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(() => this.onFiltersUpdated());

    this.filtersForm
      .get('startTime')
      ?.valueChanges.subscribe((start: TuiTime | null) => {
        if (start) {
          this.endTimeItems = this.generateTimeItems().filter(
            (t) => t.toAbsoluteMilliseconds() > start.toAbsoluteMilliseconds()
          );
          const first = this.endTimeItems[0] ?? null;
          this.filtersForm
            .get('endTime')
            ?.setValue(first, { emitEvent: false });
        } else {
          this.endTimeItems = this.generateTimeItems();
          this.filtersForm.get('endTime')?.setValue(null, { emitEvent: false });
        }
        this.onFiltersUpdated();
      });

    this.filtersForm
      .get('dateRange')
      ?.valueChanges.subscribe(() => this.handleDateRangeChange());

    (
      ['endTime', 'zones', 'products', 'clients', 'drivers', 'status'] as const
    ).forEach((ctrl) => {
      this.filtersForm
        .get(ctrl)
        ?.valueChanges.subscribe(() => this.onFiltersUpdated());
    });
  }

  private onFiltersUpdated(index = 0): void {
    this.index = index;

    if (this.useApiFiltering) {
      this.emitFilters();
      return;
    }

    this.applyAllFiltersAndPaginate();
  }

  filterData(): void {
    this.index = 0;
    this.onFiltersUpdated();
  }

  filterDataBySelect(index?: number): void {
    this.onFiltersUpdated(index ?? 0);
  }

  private applyAllFiltersAndPaginate(): void {
    let data = [...this.allServices];

    if (!this.useApiFiltering) {
      data = this.applySearchFilter(data);
      data = this.applyClientFilter(data);
      data = this.applyZoneFilter(data);
      data = this.applyProductFilter(data);
      data = this.applyDateFilter(data);
      data = this.applyTimeFilter(data);
      data = this.applyStatusFilter(data);
      data = this.applyDriverFilter(data);
    }

    data = this.sortBy(data);
    this.paginateAndEmit(data);
  }

  private applySearchFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const term = (this.filtersForm.get('search')?.value ?? '')
      .toString()
      .trim()
      .toLowerCase();
    if (!term) return data;

    const unassigned = this.translate
      .instant('historical_table.unassigned')
      .toLowerCase();

    return data.filter((item) => {
      const fields = [
        item.licensePlate?.toLowerCase(),
        item.orderHash?.toLowerCase(),
        (item.driverName?.trim() || unassigned).toLowerCase(),
        item.clientName?.toLowerCase(),
      ];
      return fields.some((f) => f && f.includes(term));
    });
  }

  private applyClientFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: string[] = this.filtersForm.get('clients')?.value ?? [];
    if (!selected.length) return data;
    const set = new Set(selected);
    return data.filter(
      (item) => set.has(item.clientName) || (!!item.clientId && set.has(item.clientId))
    );
  }

  private applyZoneFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const zones: string[] | null = this.filtersForm.get('zones')?.value ?? null;
    if (!zones?.length) return data;
    return data.filter((item) => zones.includes(item.zoneName));
  }

  private applyProductFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: string[] = this.filtersForm.get('products')?.value ?? [];
    if (!selected.length) return data;
    const set = new Set(selected);
    return data.filter((item) => {
      const display = item.productName
        ? this.translateProduct(item.productName)
        : '';
      return display ? set.has(display) : false;
    });
  }

  private applyDateFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const range = this.filtersForm.get('dateRange')?.value as
      | [Date, Date]
      | null;
    if (!range) return data;

    const [start, end] = range;
    const startMs = new Date(start).setHours(0, 0, 0, 0);
    const endMs = new Date(end).setHours(23, 59, 59, 999);

    return data.filter((s) => {
      const startIso = (s.assignedStartDate ?? '').split(' ')[0];
      const t = new Date(startIso).getTime();
      return !Number.isNaN(t) && t >= startMs && t <= endMs;
    });
  }

  private handleDateRangeChange(): void {
    const control = this.filtersForm.get('dateRange');
    const range = control?.value as
      | [Date, Date]
      | null;

    if (!range || range.some((date) => !date)) {
      this.onFiltersUpdated();
      return;
    }

    const [fromDate, toDate] = range;

    if (moment(toDate).diff(moment(fromDate), 'months', true) > 1) {
      this.tuiAlertService
        .open(
          this.translate.instant(
            'service_manager.pending_service.max_date_range_exceeded',
            { months: 1 }
          ),
          {
            status: 'error',
            autoClose: true,
          }
        )
        .subscribe();

      control?.setValue(this.previousValidDateRange, { emitEvent: false });
      return;
    }

    this.previousValidDateRange = [fromDate, toDate];
    this.onFiltersUpdated();
  }

  private applyTimeFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const s: TuiTime | null = this.filtersForm.get('startTime')?.value ?? null;
    const e: TuiTime | null = this.filtersForm.get('endTime')?.value ?? null;
    if (!s || !e) return data;

    const startMinutes = s.hours * 60 + s.minutes;
    const endMinutes = e.hours * 60 + e.minutes;

    return data.filter((item) => {
      const startIso = (item.assignedStartDate ?? '').split(' ')[0];
      const endIso = (item.endDate ?? '').split(' ')[0];

      const startDate = new Date(startIso);
      const endDate = new Date(endIso);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
        return false;

      const itemStartMins = startDate.getHours() * 60 + startDate.getMinutes();
      const itemEndMins = endDate.getHours() * 60 + endDate.getMinutes();

      return itemStartMins >= startMinutes && itemEndMins <= endMinutes;
    });
  }

  private applyStatusFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: number[] = this.filtersForm.get('status')?.value ?? [];
    if (!selected.length) return data;
    const set = new Set(selected);
    return data.filter((item) => set.has(item.nodeStatus));
  }

  private applyDriverFilter(data: PoolServiceItem[]): PoolServiceItem[] {
    const selected: string[] = this.filtersForm.get('drivers')?.value ?? [];
    if (!selected.length) return data;

    const unassigned = this.translate.instant('historical_table.unassigned');
    const set = new Set(selected);

    return data.filter((item) => {
      const name = (item.driverName ?? '').trim();
      const isUnassigned = !name && set.has(unassigned);
      const isAssigned = name && set.has(name);
      return isUnassigned || isAssigned;
    });
  }

  private toValidDate(input?: string): Date | null {
    if (!input) return null;
    const raw = String(input).split(' ')[0] ?? '';
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  setSortColumn(column: SortableColumn): void {
    if (this.sortedColumn !== column) {
      this.sortedColumn = column;
      this.sortDirection = 1;
    } else if (this.sortDirection === 1) {
      this.sortDirection = -1;
    } else {
      this.sortedColumn = '';
      this.sortDirection = 1;
    }
    this.applyAllFiltersAndPaginate();
  }

  onSort(dir: SortDirection): void {
    this.sortDirection = dir;
    this.applyAllFiltersAndPaginate();
  }

  private sortBy(data: PoolServiceItem[]): PoolServiceItem[] {
    if (!this.sortedColumn) return data;

    type Comparable = number | string;

    const col = this.sortedColumn;
    const dir = this.sortDirection;

    const getValue = (item: PoolServiceItem): Comparable => {
      switch (col) {
        case 'assignedStartDate': {
          const t = Date.parse((item.assignedStartDate ?? '').split(' ')[0]);
          return Number.isNaN(t) ? 0 : t;
        }
        case 'startHour': {
          const d = new Date((item.assignedStartDate ?? '').split(' ')[0]);
          return Number.isNaN(d.getTime())
            ? 0
            : d.getHours() * 60 + d.getMinutes();
        }
        case 'nodeStatus':
          return item.nodeStatus ?? 0;
        case 'totalPrice':
          return item.totalPrice ?? 0;

        case 'productName':
          return item.productName
            ? this.translateProduct(item.productName)
            : '';

        case 'licensePlate':
          return item.licensePlate ?? '';
        case 'orderHash':
          return item.orderHash ?? '';
        case 'zoneName':
          return item.zoneName ?? '';
        case 'clientName':
          return item.clientName ?? '';
        case 'driverName':
          return item.driverName ?? '';

        default:
          return '';
      }
    };

    const cmp = (a: Comparable, b: Comparable): number => {
      if (typeof a === 'string' && typeof b === 'string') {
        return a.localeCompare(b, 'es', { sensitivity: 'base' });
      }
      return Number(a) - Number(b);
    };

    return [...data].sort((a, b) => {
      const A = getValue(a);
      const B = getValue(b);
      const res = cmp(A, B);
      return dir === -1 ? -res : res;
    });
  }

  private paginateAndEmit(data: PoolServiceItem[]): void {
    if (this.useApiFiltering) {
      this.syncApiPaginationState();
      this.paginatedData = data;
      this.countEvent.emit(this.totalItems);
      return;
    }

    this.length = Math.max(1, Math.ceil(data.length / this.pageSize));
    if (this.index >= this.length) this.index = 0;

    const start = this.index * this.pageSize;
    this.paginatedData = data.slice(start, start + this.pageSize);

    this.countEvent.emit(data.length);
  }

  goToPage(pageIndex: number): void {
    if (this.useApiFiltering) {
      this.onFiltersUpdated(pageIndex);
      return;
    }

    this.index = pageIndex;
    this.applyAllFiltersAndPaginate();
  }

  private generateTimeItems(): TuiTime[] {
    const items: TuiTime[] = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        items.push(new TuiTime(h, m));
      }
    }
    return items;
  }

  formatHourRange(start: string, end: string): string {
    const s = this.toValidDate(start);
    const e = this.toValidDate(end);
    if (!s || !e) return '-';

    const fmt = (d: Date) =>
      d.toLocaleTimeString('es-ES', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });

    return `${fmt(s)}-${fmt(e)}`;
  }

  private isBlank(value: unknown): boolean {
    return (
      value === null ||
      value === undefined ||
      (typeof value === 'string' && value.trim() === '')
    );
  }

  display(value: unknown): string {
    return this.isBlank(value) ? '-' : String(value);
  }

  formatDate(dateStr: string): string {
    const d = this.toValidDate(dateStr);
    if (!d) return '-';

    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(d);
  }

  isWithin48Hours(dateStr: string, driverName: string): boolean {
    const d = new Date((dateStr ?? '').split(' ')[0]);
    const now = new Date();
    const diffHours = (d.getTime() - now.getTime()) / (1000 * 60 * 60);
    const withinNext48 = diffHours <= 48 && diffHours > 0;
    const inPast = d < now;
    return (withinNext48 || inPast) && !driverName;
  }

  getOrderStatusMeta(statusCode: number): OrderStatusMeta {
    const key = STATUS_CODE_MAP[statusCode];
    return (
      ORDER_STATUS_MAP[key] ?? {
        label: 'Desconocido',
        tuiStatus: 'neutral',
        cssClass: '',
      }
    );
  }

  translateProduct(name: string): string {
    if (!name) return this.translate.instant('historical_table.no_product');
    const key = 'common.services.' + name;
    const translated = this.translate.instant(key);
    return translated === key ? name : translated;
  }

  getClientById(_id?: string): string | null {
    return null;
  }

  viewDetail({ orderHash }: { orderHash: string }): void {
    this.historyServicesSvc.setData({ orderHash });
    const tree: UrlTree = this.router.createUrlTree(
      ['/service-manager/detail-service'],
      { queryParams: { orderHash } }
    );
    window.open(`${window.location.origin}/#/${tree}`, '_blank');
  }

  exportSelected(): void {
    if (this.exportCsv) this.exportCSV();
    if (this.exportXlsx) this.exportXLSX();
  }

  private exportCSV(): void {
    const t = this.translate;
    const today = this.safeDateForFilename(new Date(), 'Europe/Madrid');
    const filename = this.buildFilename(
      'historical_table.export_filename_historical_services',
      today,
      'csv'
    );

    const headers = [
      t.instant('historical_table.plate_input_table'),
      t.instant('historical_table.hour_input_table'),
      t.instant('historical_table.hash_input_table'),
      t.instant('historical_table.zone_input_table'),
      t.instant('historical_table.date_input_table'),
      t.instant('historical_table.product_input_table'),
      t.instant('historical_table.status_input_table'),
      t.instant('historical_table.client_input_table'),
      t.instant('historical_table.column_driver'),
      t.instant('historical_table.column_price'),
      t.instant('historical_table.column_price_without_vat'),
      t.instant('historical_table.column_pool_price'),
      t.instant('historical_table.driver_pickup_start'),
      t.instant('historical_table.driver_pickup_end'),
      t.instant('historical_table.driver_return_start'),
      t.instant('historical_table.driver_return_end'),
    ].map((h) => this.escapeCsv(h));

    const rows = this.allServices.map((r) =>
      [
        r.licensePlate,
        this.formatHourRange(r.assignedStartDate, r.endDate),
        r.orderHash,
        r.zoneName,
        this.formatDate(r.assignedStartDate),
        this.translateProduct(r.productName ?? ''),
        t.instant(this.getOrderStatusMeta(r.nodeStatus).label),
        r.clientName,
        r.driverName || t.instant('historical_table.unassigned'),
        this.escapeCsv(
          this.formatPriceWithSymbol(r.totalPrice, r.currencySymbol)
        ),
        this.escapeCsv(
          this.formatPriceWithSymbol(
            (r as any).priceWithoutTax ?? r.priceWithoutTax,
            r.currencySymbol
          )
        ),
        this.escapeCsv(
          this.formatPriceWithSymbol(
            (r as any).poolPrice ?? r.poolPrice,
            r.currencySymbol
          )
        ),
        ...getDriverStageTimes(r.driverEvents),
      ].map((v) => String(v))
    );

    const csv = [headers, ...rows].map((l) => l.join(';')).join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
  }

  private formatPriceWithSymbol(
    value: number | null | undefined,
    currencySymbol?: string
  ): string {
    const n = Number(value ?? 0);
    const symbol = (currencySymbol ?? '').toString().trim();
    return symbol ? `${n.toFixed(2)} ${symbol}` : n.toFixed(2);
  }

  private exportXLSX(): void {
    const t = this.translate;
    const today = this.safeDateForFilename(new Date(), 'Europe/Madrid');

    const filename = this.buildFilename(
      'historical_table.export_filename_historical_services',
      today,
      'xlsx'
    );

    const headers = [
      t.instant('historical_table.plate_input_table'),
      t.instant('historical_table.hour_input_table'),
      t.instant('historical_table.hash_input_table'),
      t.instant('historical_table.zone_input_table'),
      t.instant('historical_table.date_input_table'),
      t.instant('historical_table.product_input_table'),
      t.instant('historical_table.status_input_table'),
      t.instant('historical_table.client_input_table'),
      t.instant('historical_table.column_driver'),
      t.instant('historical_table.column_price'),
      t.instant('historical_table.column_price_without_vat'),
      t.instant('historical_table.column_pool_price'),
      t.instant('historical_table.driver_pickup_start'),
      t.instant('historical_table.driver_pickup_end'),
      t.instant('historical_table.driver_return_start'),
      t.instant('historical_table.driver_return_end'),
    ];

    const rows = this.allServices.map((r) => [
      r.licensePlate,
      this.formatHourRange(r.assignedStartDate, r.endDate),
      r.orderHash,
      r.zoneName,
      this.formatDate(r.assignedStartDate),
      this.translateProduct(r.productName ?? ''),
      t.instant(this.getOrderStatusMeta(r.nodeStatus).label),
      r.clientName,
      r.driverName || t.instant('historical_table.unassigned'),
      // Aquí usamos helper para añadir símbolo (ej. "146.59 €")
      this.formatPriceWithSymbol(r.totalPrice, r.currencySymbol),
      this.formatPriceWithSymbol(
        (r as any).priceWithoutTax ?? r.priceWithoutTax,
        r.currencySymbol
      ),
      this.formatPriceWithSymbol(
        (r as any).poolPrice ?? r.poolPrice,
        r.currencySymbol
      ),
      // Horas del conductor como valor de hora (no texto) para que Excel pueda restar inicio y fin.
      ...getDriverStageTimes(r.driverEvents).map(
        (time) => timeToExcelDay(time) ?? '',
      ),
    ]);

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    applyTimeFormat(
      ws,
      headers.length - DRIVER_STAGE_COLUMNS,
      DRIVER_STAGE_COLUMNS,
    );
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Históricos');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout]), filename);
  }

  private buildFilename(
    baseKey: string,
    date: string,
    ext: 'csv' | 'xlsx'
  ): string {
    const raw = this.translate.instant(baseKey, { date });

    const withoutExt = raw.replace(/\.(csv|xlsx|xls)$/i, '');

    const sanitized = withoutExt
      .replace(/[<>:"\\|?*]+/g, '')
      .replace(/[\/]+/g, '-')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\s/g, '-');

    return `${sanitized}.${ext}`;
  }

  private safeDateForFilename(d: Date, timeZone: string): string {
    const fmt = new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone,
    });
    return fmt.format(d).replace(/\//g, '-');
  }

  private escapeCsv(value: string): string {
    return /[",\n;]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  }

  private toggleFiltersDisabled(loading: boolean): void {
    if (!this.filtersForm) return;
    if (loading) {
      this.filtersForm.disable({ emitEvent: false });
    } else {
      this.filtersForm.enable({ emitEvent: false });
    }
  }

  trackByOrderHash = (_: number, s: { orderHash: string }) => s.orderHash;

  private emitFilters(): void {
    this.filtersChange.emit(this.buildFiltersPayload());
  }

  private syncApiPaginationState(): void {
    if (!this.useApiFiltering) {
      return;
    }

    this.length = Math.max(1, Math.ceil(this.totalItems / this.serverPageSize));
    this.index = Math.min(this.currentPage, Math.max(0, this.length - 1));
  }

  private buildFiltersPayload(): GetPaginatedFreelancePoolServicesRequest {
    const range = this.filtersForm.get('dateRange')?.value as
      | [Date, Date]
      | null;
    const [fromDate, toDate] = range ?? this.getDefaultDateRange();

    return {
      fromDate: this.formatDateOnly(fromDate),
      toDate: this.formatDateOnly(toDate),
      pageNumber: this.index + 1,
      pageSize: this.serverPageSize,
      zoneGuids: this.getArrayValue('zones'),
      productsName: this.getArrayValue('products'),
      clientsGuid: this.getArrayValue('clients'),
      serviceStatus: this.getNumberArrayValue('status'),
      driverNames: this.getArrayValue('drivers'),
      serviceFilter: (this.filtersForm.get('search')?.value ?? '')
        .toString()
        .trim(),
    };
  }

  private getArrayValue(controlName: string): string[] {
    const value = this.filtersForm.get(controlName)?.value;
    return Array.isArray(value) ? value.filter(Boolean).map(String) : [];
  }

  private getNumberArrayValue(controlName: string): number[] {
    return this.getArrayValue(controlName).map(Number).filter((v) => !Number.isNaN(v));
  }

  private getDefaultDateRange(): [Date, Date] {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(toDate.getDate() - 15);
    return [fromDate, toDate];
  }

  private formatDateOnly(date: Date): string {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  translateProductKey(name: string): string {
    return this.historyServicesSvc.productsCodes[this.translate.currentLang][
      name
    ];
  }
}
